'use client';

import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { getMarketName, type MarketCode, type Sport, type PlayerWithMarkets } from '@sharpr/types';
import type { OverlayTarget } from '../types/research';
import { buildOverlayTarget } from '../utils/buildOverlayTarget';
import { aggregateMarketLines } from '@/lib/research/buildResearchTarget';
import { resolvePositionType } from '../utils/playerTaxonomy';
import {
  clearStoredSlipDestination,
  getStoredSlipDestination,
  type SlipDestination,
} from '../utils/slipDestinationStorage';
import {
  clearSlipDestinationCookie,
  readSlipDestinationCookie,
} from '../utils/slipDestinationCookie';

/**
 * Inputs for the slip-deep-link consumer. The hook reads its destination
 * state internally (cookie first, then localStorage) so the caller's dep
 * array stays compact — just the semantic inputs that actually change
 * consumer behaviour.
 */
export interface UseSlipDeepLinkParams {
  /** Player rows the consumer searches against. Use the recomputed
   *  (best-lines-resolved, conditionals-applied) list so the overlay
   *  opens with the same data the row click would have produced. */
  players: PlayerWithMarkets[];
  /** Current page sport. Destinations whose sport doesn't match the page
   *  are left in place (don't auto-open an NBA overlay on `/mlb`). */
  sport: Sport;
  /** When true, page is rendering the upgrade banner. Surface a "subscribe
   *  to track this pick" toast instead of opening a projection-stripped
   *  overlay behind the upgrade gate. */
  noSportAccess: boolean;
  /** Markets the user has hidden via per-sport preferences. When the slip
   *  market is on this list, the missing-data toast points at settings
   *  rather than implying the pick expired. */
  userHiddenMarkets: Set<MarketCode>;
  /** Add the slip's player to the selection set so they remain visible in
   *  the selected-players table once the user closes the overlay. */
  selectPlayer: (playerId: string) => void;
  /** Open the line-movement overlay with the built target. */
  openOverlay: (target: OverlayTarget) => void;
}

type DestinationCarrier = 'cookie' | 'storage';

/**
 * Share-slip deep-link consumer. Two carriers feed the same flow:
 *   1. Authed recipients: `/s/<id>` server-sets a 60s cookie with the slip
 *      destination and redirects to clean `/<sport>`. The cookie wins
 *      because it's the most recent intent (the user just clicked the
 *      share link).
 *   2. Unauthed-then-signed-up recipients: SignUpForm/LoginForm stashed
 *      the destination in localStorage on form submit. Falls through here
 *      after checkout completes.
 *
 * Either way we look up the player+market in the supplied list, auto-select
 * the player, build an OverlayTarget matching the cell-click path in
 * TableRow, open the overlay, then clear whichever carrier supplied the
 * destination so back-nav / refresh / sport-toggle don't re-trigger.
 *
 * Both carriers self-clear on read; no URL scrubbing required (URL never
 * carried slip params under the cookie design). The `consumed` ref still
 * gates the re-fire that would otherwise happen when the player list
 * re-renders for unrelated reasons (re-poll, conditional toggles).
 */
export function useSlipDeepLink({
  players,
  sport,
  noSportAccess,
  userHiddenMarkets,
  selectPlayer,
  openOverlay,
}: UseSlipDeepLinkParams): void {
  const consumed = useRef(false);

  useEffect(() => {
    if (consumed.current) return;
    if (players.length === 0) return;

    // Cookie wins over localStorage: the cookie is set by the most recent
    // server-side intent (just-clicked share link), while localStorage may
    // hold a stale destination from a signup flow the user already
    // completed but never landed on.
    let destination: SlipDestination | null = null;
    let carrier: DestinationCarrier | null = null;
    const cookieDest = readSlipDestinationCookie();
    if (cookieDest && cookieDest.sport === sport) {
      destination = cookieDest;
      carrier = 'cookie';
    } else {
      const stored = getStoredSlipDestination();
      if (stored && stored.sport === sport) {
        destination = stored;
        carrier = 'storage';
      }
    }
    if (!destination || !carrier) return;

    const slipPlayer = destination.playerId;
    const slipMarket = destination.market as MarketCode;
    const slipBook = destination.book;
    const slipSide = destination.side;

    const clearCarrier = () => {
      if (carrier === 'cookie') clearSlipDestinationCookie();
      else clearStoredSlipDestination();
    };

    // Sport gated: user lacks a subscription for this sport, so the page is
    // already showing the upgrade banner/card. Don't open the overlay onto
    // projection-stripped data; surface a toast that ties the share intent
    // to the visible upgrade CTA.
    if (noSportAccess) {
      consumed.current = true;
      clearCarrier();
      toast.info(`Subscribe to ${sport.toUpperCase()} to track this pick`, {
        description: 'Add the sport above to see line movement, projections, and +EV edges.',
      });
      return;
    }

    const player = players.find((p) => p.id === slipPlayer);
    const marketData = player?.markets[slipMarket];
    if (!player || !marketData) {
      // Player or market missing from current data (game finished, removed
      // by admin, user-hidden via market preferences, odds pulled, etc.).
      // Distinguish player-gone vs market-gone so the message points at
      // the actual gap.
      consumed.current = true;
      clearCarrier();
      const isUserHidden = !!player && userHiddenMarkets.has(slipMarket);
      toast.error('This pick is no longer available', {
        description: isUserHidden
          ? 'This market is hidden in your settings. Unhide it to see the line.'
          : !player
            ? 'The game may have started or the player was removed from the slate.'
            : 'Odds for this market were pulled. Refresh in a minute or try a different book.',
      });
      return;
    }

    selectPlayer(player.id);
    const pos = player.position;
    openOverlay(
      buildOverlayTarget({
        playerName: player.name,
        market: getMarketName(slipMarket),
        marketCode: slipMarket,
        imageUrl: player.imageUrl,
        playerId: player.id,
        projection: marketData.projection,
        pmf: marketData.pmf,
        bestLines: marketData.bestLines,
        books: marketData.books,
        projectedMinutes: marketData.projectedMinutes,
        clickedBook: slipBook,
        clickedSide: slipSide,
        team: player.team,
        opponent: player.game?.opp,
        commenceTime: player.game?.commenceTime,
        gameId: player.game?.id,
        isHome: player.game?.isHome,
        position: pos,
        positionType: resolvePositionType(pos, slipMarket),
        teamNotes: marketData.teamNotes ?? player.teamNotes,
        playerNotes: player.playerNotes,
        lineup: marketData.lineup ?? player.lineup,
        marketProjections: aggregateMarketLines(player).marketProjections,
      }),
    );
    consumed.current = true;
    clearCarrier();
  }, [players, selectPlayer, openOverlay, sport, userHiddenMarkets, noSportAccess]);
}
